import { useEffect } from 'react';

import type { ProjectBackgroundEntry } from './projectsBackgroundRegistry';

interface UseProjectBackgroundRegistrationArgs {
  projectId: string;
  projectTitle: string;
  agentRunId?: string;
  runFailed: boolean;
  runClosed: boolean;
  enabled?: boolean;
  /** The app's one registry, bound to the desktop poke where it is created. */
  registerProjectBackground: (entry: Omit<ProjectBackgroundEntry, 'updatedAt'>) => void;
  unregisterProjectBackground: (agentRunId: string) => void;
}

export const useProjectBackgroundRegistration = ({
  projectId,
  projectTitle,
  agentRunId,
  runFailed,
  runClosed,
  enabled = true,
  registerProjectBackground,
  unregisterProjectBackground,
}: UseProjectBackgroundRegistrationArgs) => {
  useEffect(() => {
    if (!agentRunId) return;
    // The registry stays alive across navigation: deregistration is terminal
    // run status, not unmount. A cleanup here would drop an owner's desktop
    // session when they leave the chat. A locked viewer never registers, and
    // drops a session claimed before the grant resolved.
    if (!enabled) {
      unregisterProjectBackground(agentRunId);
      return;
    }
    registerProjectBackground({
      projectId,
      projectTitle,
      agentRunId,
      origin: 'main',
    });
  }, [
    enabled,
    agentRunId,
    projectId,
    projectTitle,
    registerProjectBackground,
    unregisterProjectBackground,
  ]);

  useEffect(() => {
    if (agentRunId && (runFailed || runClosed)) {
      unregisterProjectBackground(agentRunId);
    }
  }, [agentRunId, runFailed, runClosed, unregisterProjectBackground]);
};
